<script setup lang="ts">
import type { PickupItems, WidgetBody, SliderItem } from '@/types/appTypes'

type NaruhodoTodayItem = {
  title: string
  article_slug: string
  body: string
}

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()
const storeArticleSlider = useStoreArticleSlider()
const storeUser = useStoreUser()

definePageMeta({ layout: 'layout-top' })

const pageData = {
  mainSliderItems: [] as SliderItem[],
  bannerSliderItems: [] as SliderItem[],
  eventItems: [] as Article[],
  eventQuery: {} as Record<string, any>,
  pickupItems: [] as PickupItems[],
}

const refData = reactive({
  presentItems: [] as Article[],
  newsItems: [] as Article[],
  gakugeiinItems: [] as Article[],
  naruhodoItems: [] as Article[],
  naruhodoTodayItems: [] as NaruhodoTodayItem[],
  naruhodoToday: {
    isFind: false,
    dateMonth: null,
    dateDay: null,
  },
  sideRecommends: [] as SliderItem[],
  sideBottom: null as WidgetBody | null,
})

const makePickupItems = (item: Article): PickupItems[] => {
  const newItems = [] as PickupItems[]
  if (item?.relations) {
    for (let i = 0; i < item.relations.length; i++) {
      const newItem = {} as PickupItems
      const article = item.relations[i].article
      const imageUrl = storeIndex.getArticleEyeCatchImageURL(article.eye_catch_image_object)

      if (imageUrl && (
        article.article_type_slug === 'report'
        || article.article_type_slug === 'special'
        || article.article_type_slug === 'news'
      )) {
        newItem.article_slug = article.article_slug
        newItem.article_type_slug = article.article_type_slug
        newItem.imageUrl = imageUrl
        newItem.isNoImage = imageUrl ? false : true
        newItem.title = article.title
        if (article.article_type_slug === 'report') {
          newItem.is_im_report = article.custom?.is_im_report
        }
        newItems.push(newItem)
      }
    }
  }
  return newItems
}

const makeNaruhodoToday = () => {
  if (refData.naruhodoItems?.length) {
    //  なるほどは、本日のリストを表示
    //  なければ、10件中、昔ををたどる
    const searchDay = useDayjs()
    let lastFind = null
    for (let i = 0; i < refData.naruhodoItems.length; i++) {
      //  今日を探す
      const item = refData.naruhodoItems[i]
      const djs = useDayjs(item.open_date)
      if (searchDay.isSame(djs, 'day')) {
        const newItem = {} as NaruhodoTodayItem
        newItem.title = item.title
        newItem.article_slug = item.article_slug
        const json = JSON.parse(item.body_json)
        if (json.body && json.body.value) {
          newItem.body = app.$stripHtmlTag(json.body.value)
          newItem.body = newItem.body.replace(/\[url=(.+?)\](.+?)\[\/url\]/g, '$2')
        }
        refData.naruhodoTodayItems.push(newItem)
        lastFind = i
      }
    }
    if (lastFind !== null) {
      refData.naruhodoToday.dateMonth = searchDay.month() + 1
      refData.naruhodoToday.dateDay = searchDay.date()
      refData.naruhodoToday.isFind = true
    } else {
      //  過去を探す
      for (let i = 0; i < refData.naruhodoItems.length; i++) {
        const item = refData.naruhodoItems[i]
        const djs = useDayjs(item.open_date)
        if (searchDay.isAfter(djs, 'day')) {
          const newItem = {} as NaruhodoTodayItem
          newItem.title = item.title
          newItem.article_slug = item.article_slug
          const json = JSON.parse(item.body_json)
          if (json.body && json.body.value) {
            newItem.body = app.$stripHtmlTag(json.body.value)
            newItem.body = newItem.body.replace(/\[url=(.+?)\](.+?)\[\/url\]/g, '$2')
          }
          refData.naruhodoTodayItems.push(newItem)
          refData.naruhodoToday.dateMonth = djs.month() + 1
          refData.naruhodoToday.dateDay = djs.date()
          refData.naruhodoToday.isFind = true
          break
        }
      }
    }
  }
}

const initPage = async () => {
  const eventOptions: KjdsFetchListOptions = { query: {} }
  if (route.query.term === 'all') {
    // 今日以降のイベント
    eventOptions.query = { dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00') }
    eventOptions.sort = 'pv_last_week'
  } else if (route.query.term === 'start') {
    eventOptions.query = { start: useDayjs().format('YYYY/MM/DD 23:59:59') }
    eventOptions.sort = 'term_from'
  } else if (route.query.term === 'end') {
    eventOptions.query = {
      dateBefore: useDayjs().format('YYYY/MM/DD 23:59:59'),
      dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00'),
    }
    eventOptions.sort = 'term_to'
  } else {
    eventOptions.query = {
      dateBefore: useDayjs().format('YYYY/MM/DD 23:59:59'),
      dateAfter: useDayjs().format('YYYY/MM/DD 00:00:00'),
    }
  }

  // レイアウトシフトが起きにくい、1画面ちょいを同期読み込み。それ以外は遅延読み込み
  // ただし、hooper(slider)は遅延をするとやっかいなので、同期読込する
  const [_resSite, _resMaster, resSlider, resBannerSlider, resEventList, resPickupList] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleSlider.fetchSlider('top_slider'),
    storeArticleSlider.fetchSlider('top_banner_slider'),
    storeArticleList.fetchList('eventTop', eventOptions),
    storeArticleList.fetchList('top_pickup_slider', { query: { article_slug: 'top_pickup_slider' } }),
  ])
  if (resSlider?.code === 200000) {
    const sliderArticle = resSlider.data.article as Article
    const sliderObjects = resSlider.data.article_objects as ArticleObject[]
    pageData.mainSliderItems = storeArticleSlider.makeSliderItems(sliderArticle, sliderObjects)
  }
  if (resBannerSlider?.code === 200000) {
    const sliderArticle = resBannerSlider.data.article as Article
    const sliderObjects = resBannerSlider.data.article_objects as ArticleObject[]
    pageData.bannerSliderItems = storeArticleSlider.makeSliderItems(sliderArticle, sliderObjects)
  }
  if (resEventList?.code === 200000) {
    pageData.eventItems = resEventList.data.articles as Article[]
  }
  if (resPickupList?.code === 200000) {
    const pickupItems = resPickupList.data.articles as Article[]
    pageData.pickupItems = makePickupItems(pickupItems[0])
  }
}
await initPage()

onMounted(async () => {
  // レイアウトシフトが起きてもよさそうなものは遅延読み込み
  const [resPresentList, resNewsList, resGakugeiinList, resNaruhodoList, resSideRecommend, resSideBottom] = await Promise.all([
    storeArticleList.fetchList('presentSide'), // サイドバーデータと共有
    storeArticleList.fetchList('newsSide'), // サイドバーデータと共有
    storeArticleList.fetchList('gakugeiinSide'), // サイドバーデータと共有
    storeArticleList.fetchList('naruhodoMain'), // 一覧ページデータと共有
    storeArticleSlider.fetchSlider('side_recommend'),
    storeArticleList.fetchWidgetBody('side_bottom'),
  ])
  if (resPresentList?.code === 200000) {
    refData.presentItems = resPresentList.data.articles as Article[]
  }
  if (resNewsList?.code === 200000) {
    refData.newsItems = resNewsList.data.articles as Article[]
  }
  if (resGakugeiinList?.code === 200000) {
    refData.gakugeiinItems = resGakugeiinList.data.articles as Article[]
  }
  if (resNaruhodoList?.code === 200000) {
    refData.naruhodoItems = resNaruhodoList.data.articles as Article[]
    makeNaruhodoToday()
  }
  if (resSideRecommend?.data) {
    const sliderArticle = resSideRecommend.data.article as Article
    const sliderObjects = resSideRecommend.data.article_objects as ArticleObject[]
    refData.sideRecommends = storeArticleSlider.makeSliderItems(sliderArticle, sliderObjects)
  }
  if (resSideBottom?.data) {
    refData.sideBottom = resSideBottom.data
  }
})

const runtimeConfig = useRuntimeConfig().public
const fullTitle = '美術館・博物館・ミュージアムはアイエム［インターネットミュージアム］'
const twitterDescription = '美術館・博物館・ミュージアムやアートのニュースをいち早くお届け！　実際に現場で取材したニュースを、美しい写真とともにお送りします。美術館・博物館・アートのニュースはアイエムで！'

useHead({
  title: fullTitle,
  titleTemplate: '',
  meta: [
    {
      key: 'og:url',
      property: 'og:url',
      content: `${runtimeConfig.baseOrigin}${route.fullPath}`,
    },
    {
      key: 'og:title',
      property: 'og:title',
      content: fullTitle,
    },
    {
      key: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      key: 'twitter:title',
      name: 'twitter:title',
      content: fullTitle,
    },
    {
      key: 'twitter:description',
      name: 'twitter:description',
      content: twitterDescription,
    },
    {
      key: 'twitter:image',
      name: 'twitter:image',
      content: 'https://www.museum.or.jp/images/og_image.jpg',
    },
  ],
})
</script>

<template>
  <div class="p-topPage">
    <div class="p-topPage_comment">
      <h1 class="p-topPage_comment_text">
        日本最大級のミュージアム情報サイト<br>
        全国のおすすめ美術館・博物館・展覧会を掲載！
      </h1>
    </div>
    <!-- =========================================================== -->
    <!-- image slider -->
    <div class="p-topPage_secFirst l-topContent_full">
      <TopSlider
        v-bind:items="pageData.mainSliderItems"
      />
    </div>

    <!-- =========================================================== -->
    <!-- topic banner -->
    <div
      v-if="pageData.bannerSliderItems?.length"
      class="p-topPage_bannerGroup l-topContent_full"
    >
      <TopBannerSlider
        v-bind:items="pageData.bannerSliderItems"
      />
    </div>

    <!-- =========================================================== -->
    <!-- member registration -->
    <ClientOnly>
      <div
        v-if="!storeUser.state.isLogin"
        class="p-topPage_secMember l-topContent_full c-memberPanel"
      >
        <TopRegisterNotice />
      </div>
    </ClientOnly>

    <!-- =========================================================== -->
    <!-- event list -->
    <div class="p-topPage_secEvent l-topContent">
      <TopEventSection
        header="おすすめ展覧会情報"
        v-bind:items="pageData.eventItems"
      />
    </div>

    <div
      v-if="!storeUser.state.isLogin"
      class="p-topPage_secMember p-topPage_secMember_margin l-topContent_full c-memberPanel"
    >
      <TopRegisterNotice />
    </div>

    <!-- =========================================================== -->
    <!-- present list -->
    <div class="p-topPage_secPresent l-topContent">
      <TopSectionHeader
        header="ご招待券プレゼント"
      />
      <div class="p-topPage_secPresent_list">
        <template
          v-for="(item, index) in refData.presentItems"
        >
          <PresentTopItem
            v-if="index < 2"
            v-bind:key="item.id"
            class="p-topPage_secPresent_list_item"
            v-bind:item="item"
          />
        </template>
      </div>
      <div class="c-button">
        <ArrowLink
          to="/present"
          label="もっとみる"
          bold
        />
      </div>
    </div>

    <!-- =========================================================== -->
    <!-- report & special slider -->
    <div class="p-topPage_secReport l-topContent_full">
      <TopReportSlider
        v-bind:items="pageData.pickupItems"
      />
    </div>

    <!-- member push -->
    <ClientOnly>
      <div
        v-if="!storeUser.state.isLogin"
        class="p-topPage_secMember l-topContent_full c-memberPanel"
      >
        <TopRegisterNotice />
      </div>
    </ClientOnly>

    <!-- =========================================================== -->
    <!-- news & gakugeiin & naruhodo & banner -->
    <div class="p-topPage_lowerContent l-topContent">
      <div class="p-topPage_lowerContent_main">
        <div class="p-topNews">
          <SideHeading
            class="p-topNews_head"
            header="ニュース"
            v-bind:link-to="'/news'"
          />
          <div class="p-topNews_list">
            <template
              v-for="item in refData.newsItems"
              v-bind:key="item.id"
            >
              <div
                class="p-topNews_item"
              >
                <div class="p-topNews_item_date">
                  {{ app.$getPeriodDayLabelNoDD(item.open_date) }}
                </div>
                <div class="p-topNews_item_text">
                  <nuxt-link
                    class="p-topNews_item_text_title c-clampLines is-clampline2"
                    v-bind:to="`/news/${item.article_slug}`"
                  >
                    {{ item.title }}
                  </nuxt-link>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="p-topNews">
          <SideHeading
            class="p-topNews_head"
            header="学芸員募集"
            v-bind:link-to="'/gakugeiin'"
          />
          <div class="p-topNews_list">
            <template
              v-for="item in refData.gakugeiinItems"
              v-bind:key="item.id"
            >
              <div
                class="p-topNews_item"
              >
                <div class="p-topNews_item_date">
                  {{ app.$getPeriodDayLabelNoDD(item.open_date) }}
                </div>
                <div class="p-topNews_item_text">
                  <nuxt-link
                    class="p-topNews_item_text_title c-clampLines is-clampline2"
                    v-bind:to="`/gakugeiin/${item.article_slug}`"
                  >
                    {{ item.title }}
                  </nuxt-link>
                  <div
                    v-if="item.term_to"
                    class="p-topNews_item_text_limitDate"
                  >
                    【締切】{{ app.$getPeriodDayLabelNoDD(item.term_to) }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="p-topPage_lowerContent_side">
        <div
          v-if="refData.naruhodoTodayItems.length"
          class="c-topNaruhodo"
        >
          <div class="c-topNaruhodo_naruhodoLine">
            <div class="c-topNaruhodo_naruhodoLine_date">
              <div class="c-topNaruhodo_naruhodoLine_date_month">
                {{ refData.naruhodoToday.dateMonth }}
              </div>
              <div class="c-topNaruhodo_naruhodoLine_date_slash">
                /
              </div>
              <div class="c-topNaruhodo_naruhodoLine_date_day">
                {{ refData.naruhodoToday.dateDay }}
              </div>
            </div>
            <div class="c-topNaruhodo_naruhodoLine_content">
              なるほど<br>
              ミュージアム
            </div>
          </div>
          <div
            v-for="(item, index) in refData.naruhodoTodayItems"
            v-bind:key="index"
            class="c-topNaruhodo_item"
          >
            <div class="c-topNaruhodo_title c-clampLines is-clampline2">
              {{ item.title }}
            </div>
            <div class="c-topNaruhodo_detail c-clampLines is-clampline3">
              {{ item.body }}
            </div>
          </div>
          <div
            class="c-topNaruhodo_more c-chevRightLink"
          >
            <ArrowLink
              to="/naruhodo"
              label="もっとみる"
              bold
            />
          </div>
        </div>

        <SideBox
          class="p-topPage_lowerContent_side_linkList"
          header="おすすめのコンテンツ"
        >
          <SideLinkList
            v-bind:items="refData.sideRecommends"
          />
        </SideBox>

        <div class="c-sideItems">
          <SideBannerArea
            v-bind:data="refData.sideBottom"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

.l-topContent {
  width: var(--g_min_screen_width_pc);
  margin-right: auto;
  margin-left: auto;
  padding-right: var(--g_guide_margin_pc);
  padding-left: var(--g_guide_margin_pc);

  @include responsive.mq_down(sp) {
    width: 100%;
    margin-right: 0;
    margin-left: 0;
    padding-right: var(--g_guide_margin_sp);
    padding-left: var(--g_guide_margin_sp);
  }

  &_full {
    width: 100%;
    min-width: var(--g_min_screen_width_pc);
    padding: 0;

    @include responsive.mq_down(sp) {
      min-width: initial;
    }
  }
}

.p-topPage {
  position: relative;
  margin-bottom: 80px;
  padding-top: 40px;

  @include responsive.mq_down(sp) {
    margin-bottom: 60px;
    padding-top: 0;
  }

  :deep(.hooper) {
    height: auto;

    &:focus {
      outline: none;
    }

    *:focus {
      outline: none;
    }
  }

  :deep(.hooper-track) {
    padding: 0 !important;
    will-change: transform;
  }

  :deep(.hooper-list) {
    &:focus {
      outline: none;
    }
  }

  &_comment {
    width: 100%;
    padding: 8px 10px 6px;
    text-align: center;

    @include responsive.mq_up(pc) {
      display: none;
    }

    &_text {
      margin: 0;
      color: #898989;
      font-size: 10px;
      line-height: 12px;
    }
  }

  &_secFirst {
    position: relative;
    height: 600px;
    overflow: hidden;

    @include responsive.mq_down(sp) {
      height: 430px;
    }
  }

  &_bannerGroup {
    display: flex;
    justify-content: center;
    margin-top: 40px;

    @include responsive.mq_down(sp) {
      margin-top: 0;
    }
  }

  &_secEvent {
    margin-top: 60px;

    @include responsive.mq_down(sp) {
      margin-top: 40px;
    }
  }

  &_secMember {
    display: flex;
    justify-content: center;

    &_margin {
      margin-top: 80px;

      @include responsive.mq_down(sp) {
        margin-top: 60px;
      }
    }
  }

  &_secPresent {
    margin-top: 100px;

    @include responsive.mq_down(sp) {
      height: auto;
      margin-top: 60px;
    }

    &_list {
      display: flex;
      position: relative;
      margin-top: 37px;

      @include responsive.mq_down(sp) {
        display: block;
      }

      &_item {
        @include responsive.mq_up(pc) {
          &:nth-child(n + 3) {
            display: none;
          }
        }
      }
    }
  }

  &_secReport {
    position: relative;
    margin-top: 80px;
    padding: 70px 0 40px;
    overflow: hidden;
    background-color: var(--color_primary);

    @include responsive.mq_down(sp) {
      height: auto;
      margin-top: 60px;
      padding: 39px 0 30px;
    }

    &_title {
      margin-bottom: 39px;
      color: #fff;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 22px;
      line-height: 32px;
      text-align: center;

      @include responsive.mq_down(sp) {
        font-size: 18px;
        line-height: 22px;
      }
    }
  }

  &_lowerContent {
    display: flex;
    justify-content: space-between;
    margin-top: 80px;

    @include responsive.mq_down(sp) {
      display: block;
      margin-top: 50px;
      padding: 0 var(--g_guide_margin_sp);
    }

    &_main {
      width: 726px;
      min-height: 600px;

      @include responsive.mq_down(sp) {
        width: 100%;
        min-height: auto;
      }

      .l-container {
        box-shadow: none;
      }

      &_belowItem {
        margin-top: 35px;
      }
    }

    &_side {
      width: 336px;

      @include responsive.mq_down(sp) {
        width: 100%;
      }

      &_linkList {
        margin-top: 40px;

        @include responsive.mq_down(sp) {
          margin-top: 30px;
        }
      }
    }
  }

  &_bottomBanner {
    display: flex;
    justify-content: center;
    padding-top: 20px;
  }
}

// member
.c-memberPanel {
  display: flex;
  justify-content: center;
  width: 100%;
  background-color: #159197;

  &_body {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(1140px + (62px * 2));
    background-image: url("../assets/images/im-logo-bkg-pc.png");
    background-position: left bottom;

    @include responsive.mq_down(sp) {
      display: block;
      width: 100%;
      background-image: url("../assets/images/im-logo-bkg-sp.png");
    }

    &_box {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: var(--g_min_screen_width_pc);
      padding: 38px var(--g_guide_margin_pc);

      @include responsive.mq_down(sp) {
        display: block;
        width: 100%;
        padding: 40px 17px;
      }

      &_text {
        margin-bottom: 0;
        color: var(--g_color_text_white);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 24px;
        line-height: 1.5;

        @include responsive.mq_down(sp) {
          font-size: 18px;
        }
      }

      &_link {
        text-decoration: none;

        &:hover {
          opacity: 0.7;
        }

        @include responsive.mq_down(sp) {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        &_btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 320px;
          padding: 15.5px 0;
          border: solid 2px #ffff;
          border-radius: 4px;

          @include responsive.mq_down(sp) {
            width: 100%;
            max-width: 342px;
            margin-top: 25px;
            padding: 11.5px 10px;
          }

          &_icon {
            color: #ffff;
            font-size: 36px;
          }

          &_text {
            display: flex;
            flex-direction: column;
            align-items: center;

            &_upper {
              color: #ffff;
              font-size: 18px;
              font-weight: bold;
              line-height: 1;
            }

            &_lower {
              margin-top: 7px;
              color: #ffff;
              font-size: 12px;
              line-height: 1;
            }
          }
        }
      }
    }
  }
}

.p-topNews {
  padding: 36px 40px;
  border: 1px solid var(--g_color_border_gray);

  @include responsive.mq_down(sp) {
    padding: 15px 20px;
  }

  & + & {
    margin-top: 40px;

    @include responsive.mq_down(sp) {
      margin-top: 30px;
    }
  }

  &_head {
    margin-bottom: 38px;
  }

  &_list {
    position: relative;
  }

  &_item {
    display: flex;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--g_color_border_gray);
    font-family: "Noto Sans JP", sans-serif;

    &:first-child {
      margin-top: 0;
      padding-top: 0;
      border: none;
    }

    @include responsive.mq_down(sp) {
      display: block;
    }

    &_date {
      flex-shrink: 0;
      min-width: 105px;
      margin-right: 37px;
      color: var(--color_text_darkgray);
      font-size: 14px;
      line-height: 24px;
      white-space: nowrap;
    }

    &_text {
      flex-grow: 1;
      font-size: 16px;
      line-height: 1.5;

      &_title {
        color: var(--color_text_base);
      }

      &_limitDate {
        color: var(--color_text_red);
        font-size: 14px;
        font-weight: bold;
        line-height: 1.71;
      }
    }
  }
}

.c-topNaruhodo {
  display: flex;
  flex-direction: column;
  width: 334px;
  padding: 29px 31px;
  border: 4px solid var(--color_primary);
  border-radius: 2px;

  @include responsive.mq_down(sp) {
    width: 100%;
    height: auto;
    margin: 30px auto 0;
  }

  @include responsive.mq_down(sp) {
    padding: 29px 31px 22px;
  }

  &_more {
    display: flex;
    justify-content: center;
    margin-top: 25px;
  }

  &_naruhodoLine {
    display: flex;
    justify-content: space-between;

    @include responsive.mq_down(sp) {
      justify-content: flex-start;
    }

    &_date {
      position: relative;
      flex: 0 0 114px;
      height: 68px;
      color: var(--color_primary);
      font-family: var(--g_font_family_NanumGothic);
      font-size: 42px;
      white-space: nowrap;

      @include responsive.mq_down(sp) {
        font-size: 40px;
      }

      @include responsive.mq_down(small) {
        flex: 0 0 72px;
        font-size: 36px;
      }

      &_month {
        position: absolute;
        top: -16px;
        left: -7px;
        width: 52px;
        font-weight: bold;
        text-align: right;

        @include responsive.mq_down(small) {
          top: -8px;
          left: -26px;
        }
      }

      &_slash {
        position: absolute;
        top: -21px;
        left: 44px;
        transform: rotateZ(11deg);
        font-family: var(--g_font_family_NotoSansCJKjp);
        font-size: 57px;
        font-weight: 100;

        @include responsive.mq_down(small) {
          top: -12px;
          left: 27px;
          font-size: 48px;
        }
      }

      &_day {
        position: absolute;
        right: 0;
        bottom: 1px;
        width: 52px;
        font-weight: bold;

        @include responsive.mq_down(sp) {
          right: 1px;
          bottom: 6px;
        }

        @include responsive.mq_down(small) {
          right: -20px;
        }
      }
    }

    &_content {
      margin-top: 4px;
      margin-left: 17px;
      color: var(--color_text_base);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 22px;
      font-weight: bold;
      line-height: 28px;

      @include responsive.mq_down(sp) {
        transform: translateY(-8px);
      }
    }
  }

  &_title {
    margin-top: 18px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 18px;
    font-weight: bold;
    line-height: 1.4;

    @include responsive.mq_down(sp) {
      margin-top: 9px;
      font-size: 16px;
    }
  }

  &_detail {
    margin-top: 5px;
    margin-left: -2px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    letter-spacing: -0.08px;
    line-height: 1.4;

    @include responsive.mq_down(sp) {
      margin-top: 8px;
    }
  }

  &_link {
    margin-top: 30px;
    color: var(--g_color_text_888888);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;
    font-weight: bold;
    text-align: center;

    @include responsive.mq_down(sp) {
      font-size: 13px;
    }
  }
}

.c-button {
  display: flex;
  justify-content: center;
  margin-top: 40px;

  @include responsive.mq_down(sp) {
    margin-top: 30px;
  }
}

.c-chevRightLink {
  display: inline-flex;
  position: relative;
  align-items: center;
  overflow: visible;
  color: var(--color_primary);

  // color: red;
  font-size: 14px;

  &_icon {
    color: var(--color_primary);
  }
}

.c-sideItems {
  margin-top: 24px;

  @include responsive.mq_down(sp) {
    margin-top: 30px;
  }
}
</style>
